import React, { useEffect, useRef, useState } from 'react';
import { useEscape } from './admin/EditorModal';

type FacingMode = 'environment' | 'user';

interface CameraModalProps {
  isOpen: boolean;
  facingMode?: FacingMode;
  title?: string;
  onClose: () => void;
  onCapture: (blob: Blob) => void;
}

export const CameraModal: React.FC<CameraModalProps> = ({ isOpen, onClose, onCapture, facingMode = 'environment', title }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    async function start() {
      if (!isOpen) return;
      setError(null);
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode } });
        if (!mounted) return;
        streamRef.current = stream;
        const video = videoRef.current;
        if (!video) return;
        video.srcObject = stream;
        video.muted = true;
        (video as any).playsInline = true;
        await video.play();
        await new Promise<void>((resolve) => {
          const maybeReady = () => {
            if (video.readyState >= 2 && video.videoWidth > 0 && video.videoHeight > 0) resolve();
          };
          video.onloadedmetadata = maybeReady;
          video.oncanplay = maybeReady;
          requestAnimationFrame(maybeReady);
          setTimeout(maybeReady, 200);
        });
      } catch (e: any) {
        setError(e?.message || 'Camera not available');
      }
    }
    start();
    return () => {
      mounted = false;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
      const v = videoRef.current;
      if (v) v.srcObject = null;
    };
  }, [isOpen, facingMode]);

  // Esc closes the camera, not the editor under it.
  useEscape(onClose, isOpen);

  if (!isOpen) return null;

  const handleCapture = async () => {
    const video = videoRef.current;
    if (!video) return;
    const w = Math.max(1, video.videoWidth);
    const h = Math.max(1, video.videoHeight);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, w, h);
    canvas.toBlob((blob) => {
      if (blob) onCapture(blob);
    }, 'image/jpeg', 0.85);
  };

  return (
    <div className="fixed inset-0 z-[10065] flex items-center justify-center bg-black/70">
      <div className="relative w-full max-w-md mx-4 bg-gray-900 rounded-xl overflow-hidden border border-gray-700">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
          <div className="text-white text-lg font-semibold">{title || 'Camera'}</div>
          <button onClick={onClose} className="text-white/80 hover:text-white">✕</button>
        </div>
        <div className="w-full bg-black flex items-center justify-center" style={{ aspectRatio: '3/4' }}>
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <video ref={videoRef} className="w-full h-full object-contain" />
        </div>
        {error && <div className="px-4 py-2 text-red-400 text-sm">{error}</div>}
        <div className="px-4 py-4 flex items-center justify-center">
          <button onClick={handleCapture} className="w-16 h-16 rounded-full bg-white text-gray-900 shadow-md hover:bg-gray-100 active:bg-gray-200 flex items-center justify-center text-2xl">
            ●
          </button>
        </div>
      </div>
    </div>
  );
};

export default CameraModal;


